/**
 * SP Client Invitation Accept Page
 * Feature: 001-sp-client-management
 *
 * Public page for care seekers to accept SP invitations and create their account.
 * Mirrors the FA invitation flow (InviteSignupPage) for consistency.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '../shared/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../shared/components/ui/card';
import { Input } from '../shared/components/ui/input';
import { Label } from '../shared/components/ui/label';
import { Spinner } from '../shared/components/Loading/Spinner';
import { useInvitationDetails } from '../features/sp-client-management/hooks/useInvitationDetails';
import { useAcceptInviteSignup } from '../features/sp-client-management/hooks/useAcceptInviteSignup';

export function InvitationAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const { data: invitation, isLoading, isError, error } = useInvitationDetails(token || '');

  const { mutate: acceptInvite, isPending, error: acceptError } = useAcceptInviteSignup({
    onSuccess: (data) => {
      setSuccessEmail(data.email);

      // Store referral message if present
      if (data.referralMessage) {
        sessionStorage.setItem('referralMessage', data.referralMessage);
      }

      // Redirect to the specified URL or login page after a brief delay
      setTimeout(() => {
        if (data.redirectUrl) {
          // Store redirect URL for after login
          sessionStorage.setItem('postLoginRedirect', data.redirectUrl);
        }
        navigate(`/?email=${encodeURIComponent(data.email)}&autoLogin=true`);
      }, 2000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || !token) return;

    acceptInvite({
      token,
      firstName: invitation.clientFirstName,
      lastName: invitation.clientLastName,
      email: invitation.clientEmail,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center">
                <Spinner className="h-8 w-8" />
              </div>
              <p className="mt-4 text-center text-sm text-gray-600">
                Loading invitation...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state - invitation not found
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <XCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
              <CardTitle>Invitation Not Found</CardTitle>
              <CardDescription>
                {(error as any)?.message || 'This invitation link is invalid or has been removed.'}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center">
              <Button onClick={() => navigate('/')}>
                Go to Home
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  // Success state - account created
  if (successEmail) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <CardTitle>Account Created!</CardTitle>
              <CardDescription>
                You are now connected with {invitation.provider.companyName}.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-500">
                Redirecting you to sign in...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Expired invitation
  if (invitation.status === 'EXPIRED') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <Clock className="mx-auto h-16 w-16 text-orange-500 mb-4" />
              <CardTitle>Invitation Has Expired</CardTitle>
              <CardDescription>
                This invitation from {invitation.provider.companyName} has expired.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-500">
                Please contact {invitation.provider.companyName} to request a new invitation.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Already accepted
  if (invitation.status === 'ACCEPTED') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <CardTitle>Already Accepted</CardTitle>
              <CardDescription>
                You have already accepted this invitation and are connected with{' '}
                {invitation.provider.companyName}.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center">
              <Button onClick={() => navigate('/login')}>
                Sign In
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Already declined
  if (invitation.status === 'DECLINED') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <XCircle className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <CardTitle>Invitation Declined</CardTitle>
              <CardDescription>
                You have previously declined this invitation.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Pending invitation - show signup form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="space-y-4 text-center">
            {/* Provider Info */}
            <div className="flex flex-col items-center space-y-2">
              {invitation.provider.logoUrl && (
                <img
                  src={invitation.provider.logoUrl}
                  alt={invitation.provider.companyName}
                  className="h-16 w-16 rounded-lg object-cover"
                />
              )}
              <div>
                <p className="text-sm text-gray-600">You've been invited by</p>
                <p className="font-semibold text-gray-900">
                  {invitation.provider.companyName}
                </p>
                {invitation.provider.professionalTitle && (
                  <p className="text-xs text-gray-500">
                    {invitation.provider.professionalTitle}
                  </p>
                )}
              </div>
            </div>

            <CardTitle className="text-2xl font-bold">
              Join bQuest as a Care Seeker
            </CardTitle>
            <CardDescription>
              Complete your registration to connect with {invitation.provider.companyName}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {acceptError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-800">
                    {(acceptError as any)?.message || 'An error occurred. Please try again.'}
                  </p>
                </div>
              )}

              {/* Personal Message */}
              {invitation.personalMessage && (
                <div className="rounded-lg bg-emerald-50 p-4">
                  <p className="text-sm font-medium text-emerald-900 mb-2">
                    Personal Message:
                  </p>
                  <p className="text-sm text-gray-700 italic">
                    "{invitation.personalMessage}"
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={invitation.clientFirstName}
                  readOnly
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500">
                  You can update your name after registration
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={invitation.clientLastName}
                  readOnly
                  className="bg-gray-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={invitation.clientEmail}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500">
                  Your account will be linked to this email address
                </p>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-900">
                  <span className="font-semibold">Role:</span> Care Seeker
                </p>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Creating your account...
                  </>
                ) : (
                  'Create your account'
                )}
              </Button>

              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Sign in
                </Link>
              </div>

              <p className="text-center text-xs text-gray-500">
                This invitation expires on{' '}
                {new Date(invitation.expiresAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
